import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, MapPin, Store, X } from "lucide-react";
import BranchPopup from "../BranchPopup";
import api, { API_BASE } from "../../lib/api";

export default function BranchFinder() {
  const [pin, setPin] = useState("");
  const [branches, setBranches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState(null);
  const [selected, setSelected] = useState(null);

  const find = async (e) => {
    e.preventDefault();
    const p = pin.trim();
    if (!/^\d{6}$/.test(p)) return;
    setBusy(true);
    try {
      let all = branches;
      if (!all) {
        const r = await api.get("/branches/public");
        all = r.data;
        setBranches(all);
      }
      const exact = all.filter((b) => b.pincode === p);
      const near = all.filter((b) => b.pincode && b.pincode.slice(0, 3) === p.slice(0, 3) && b.pincode !== p);
      setMatches({ pin: p, exact, near });
    } catch { setMatches({ pin: p, exact: [], near: [] }); }
    finally { setBusy(false); }
  };

  const results = matches ? [...matches.exact, ...matches.near] : [];

  return (
    <div className="mt-4 w-full max-w-xl" data-testid="branch-finder">
      <form onSubmit={find}
            className="flex w-full items-center gap-2 rounded-full border border-stone-300 bg-white p-2 shadow-sm">
        <MapPin className="ml-3 h-5 w-5 shrink-0 text-slate-400" />
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
               inputMode="numeric" data-testid="branch-finder-input"
               placeholder="Enter your pincode to find a branch near you"
               className="w-full bg-transparent py-2.5 text-sm text-navy placeholder-slate-400 outline-none" />
        <button type="submit" disabled={busy || pin.length !== 6} data-testid="branch-finder-button"
                className="shrink-0 rounded-xl border border-emerald-400/50 bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/30 transition-colors hover:bg-emerald-500 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find Branch"}
        </button>
      </form>

      {matches && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy/70 backdrop-blur-sm sm:items-center sm:p-6"
             onClick={() => setMatches(null)} data-testid="branch-finder-results">
          <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-saffron">Branches near {matches.pin}</p>
                <h3 className="mt-1 font-heading text-lg font-extrabold text-navy">
                  {results.length ? `${results.length} branch${results.length > 1 ? "es" : ""} found` : "No branch at this pincode yet"}
                </h3>
              </div>
              <button onClick={() => setMatches(null)} data-testid="branch-finder-close" aria-label="Close"
                      className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-saffron hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {results.length > 0 && (
              <div className="mt-4 space-y-2.5" data-testid="branch-finder-list">
                {matches.exact.length > 0 && <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">At your pincode</p>}
                {matches.exact.map((b) => <FinderRow key={b.id} b={b} onOpen={setSelected} exact />)}
                {matches.near.length > 0 && <p className="pt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Nearby branches</p>}
                {matches.near.map((b) => <FinderRow key={b.id} b={b} onOpen={setSelected} />)}
              </div>
            )}

            {!results.length && (
              <p className="mt-3 text-sm leading-relaxed text-slate-500" data-testid="branch-finder-empty">
                We're expanding fast — you can still ship from anywhere.{" "}
                <Link to="/pickup-request" className="font-bold text-saffron" onClick={() => setMatches(null)}>Request a doorstep pickup</Link>{" "}
                or <Link to="/branches" className="font-bold text-saffron" onClick={() => setMatches(null)}>browse all branches</Link>.
              </p>
            )}
            {results.length > 0 && (
              <Link to="/branches" onClick={() => setMatches(null)} data-testid="branch-finder-all"
                    className="group mt-4 flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 py-2.5 text-xs font-bold text-navy hover:border-saffron hover:text-saffron">
                View all branches <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            )}
          </div>
        </div>
      )}

      <BranchPopup b={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function FinderRow({ b, onOpen, exact }) {
  return (
    <button onClick={() => onOpen(b)} data-testid={`branch-finder-item-${b.id}`}
            className="flex w-full items-center gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors hover:border-saffron/50 hover:bg-saffron/5">
      <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-navy">
        {b.photo_path ? (
          <img src={`${API_BASE}/api/branches/public/photo/${b.id}`} alt={b.shop_name} className="kenburns-a h-full w-full object-cover" />
        ) : (
          <Store className="h-5 w-5 text-white/40" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold text-navy">{b.shop_name}</span>
        <span className="mt-0.5 block text-[11px] font-semibold text-slate-500">
          {b.city}, {b.state} · {b.pincode}{exact && <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700">YOUR PINCODE</span>}
        </span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-saffron" />
    </button>
  );
}
